import React, { useEffect, useMemo } from "react"
import { Button, Input } from '@fullscreendigital/carrefour-dex-fe'
import { Modal } from 'components/core-ui/Modal'
import { useFormik } from 'formik'
import { getInitialValues, getValidationSchema } from './helpers'
import { useNotification } from 'hooks'
import { formatErrorMessage } from 'utils/formatting'
import { Grid } from '@mui/material'
import { AutocompleteOption } from 'components/volumetry/interfaces'
import { makeCoreGetRequest, makeCorePostRequest } from "utils/api"
import { useMutation, useQuery } from "react-query"
import s from 'components/core-ui/Modal/Modal.modules.scss'
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete'

interface SensitiveProductProps {
  isModalOpen: boolean
  closeModal: Function
  setShouldRefreshData: Function
  shopCodes: AutocompleteOption[]
  shopTypesOptions: AutocompleteOption[]
  canUseRules: boolean
}

type RegionResponseItem = {
  id: number
  regionName: string
  regionCode: string
}

const SensitiveProduct: React.FC<SensitiveProductProps> = props => {
  const { isModalOpen, closeModal, setShouldRefreshData, shopCodes, shopTypesOptions, canUseRules } = props
  const { showNotification } = useNotification()

  const createProduct = async (values: any) => { await makeCorePostRequest(`/shop-reception/products/sensitives`, values)}

  const { isLoading, mutate } = useMutation(createProduct, {
    onSuccess: () => {
      closeModal()
      showNotification({
        message: `Produsul a fost creat cu succes.`,
        type: 'success'
      })

      setShouldRefreshData(true)
      formik.resetForm()
      closeModal()
    },
    onError: res => {
      showNotification({
        message: `A aparut o eroare la crearea produsului. ${formatErrorMessage(res)}`,
        type: 'error'
      })
    }
  })

  const initialValues = useMemo(() => getInitialValues(), [])
  const validationSchema = useMemo(() => getValidationSchema(), [])

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values: any) => {
      const payload: any = { prodCode: values.prodCode || null }

      if (values.shopCode) {
        payload.shopCode = values.shopCode?.value || values.shopCode
      }

      if (canUseRules) {
        if (values.shopType) {
          payload.shopType = values.shopType?.value || values.shopType
        }

        if ((values.shopType?.value || values.shopType) === 'REGION') {
          payload.regionId = values.regionId?.value || values.regionId || null
        }
      }

      mutate(payload)
    }
  })

  useEffect(() => {
    if (!canUseRules) {
      formik.setFieldValue('shopType', null)
      formik.setFieldValue('regionId', null)
    }
  }, [canUseRules, isModalOpen])

  const shouldShowRegions = (formik.values.shopType?.value || formik.values.shopType) === 'REGION'

  const { data: regionsRaw = [] } = useQuery(
    ['sensitive-regions'],
    async () => {
      const res = await makeCoreGetRequest(`/shop-reception/products/sensitives/regions`)
      return (res || []) as RegionResponseItem[]
    },
    {
      enabled: !!shouldShowRegions,
      onError: err =>
        showNotification({
          message: `Nu s-au putut incarca regiunile. ${formatErrorMessage(err)}`,
          type: 'error'
        })
    }
  )

  const regionOptions: AutocompleteOption[] = useMemo(() => {
    return regionsRaw.map(r => ({
      value: r.id,
      label: r.regionName
    }))
  }, [regionsRaw])

  const isShopCodeDisabled = !!formik.values.shopType
  const isShopTypeDisabled = !!formik.values.shopCode

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={closeModal}
      modalTitle={'Adaugă regulă produse sensibile'}
      modalWidth="50%"
    >
      <Grid container spacing={2}>
        <Grid xs={12} item>
          <AsyncAutocomplete
            variant="outlined"
            label="Magazin"
            name="shopCode"
            value={formik.values.shopCode}
            options={shopCodes || []}
            onChange={option => {
              formik.setFieldValue('shopCode', option)
              if (option) {
                formik.setFieldValue('shopType', null)
                formik.setFieldValue('regionId', null)
              }
            }}
            disabled={isShopCodeDisabled}
            error={!!formik.errors.shopCode}
            helperText={formik.errors.shopCode as string}
          />
        </Grid>

        {canUseRules && (
          <Grid xs={12} item>
            <AsyncAutocomplete
              variant="outlined"
              label="Adaugă regulă produse sensibile"
              name="shopType"
              value={formik.values.shopType}
              options={shopTypesOptions || []}
              onChange={option => {
                formik.setFieldValue('shopType', option)
                if (option) {
                  formik.setFieldValue('shopCode', null)
                }
                const nextType = option?.value
                if (nextType !== 'REGION') {
                  formik.setFieldValue('regionId', null)
                }
              }}
              disabled={isShopTypeDisabled}
              error={!!formik.errors.shopType}
              helperText={formik.errors.shopType as string}
            />
          </Grid>
        )}

        {canUseRules && shouldShowRegions && (
          <Grid xs={12} item>
            <AsyncAutocomplete
              variant="outlined"
              label="Regiune"
              name="regionId"
              value={formik.values.regionId}
              options={regionOptions || []}
              onChange={option => formik.setFieldValue('regionId', option)}
              required
              error={!!formik.errors.regionId}
              helperText={formik.errors.regionId as string}
            />
          </Grid>
        )}

        <Grid xs={12} item>
          <Input
            type="text"
            value={formik.values.prodCode}
            onChange={event =>
              formik.setFieldValue('prodCode', (event.target as HTMLInputElement).value)
            }
            label="Cod produs"
            variant="outlined"
            style={{ width: '100%' }}
          />
          {!!formik.errors.prodCode && (
            <div style={{ fontSize: 12, marginTop: 4 }}>{formik.errors.prodCode as string}</div>
          )}
        </Grid>

        <Grid xs={12} item className={s.modalButtons}>
          <Button variant="outlined" label="Anuleaza" onClick={() => closeModal()} />
          <Button
            variant="contained"
            label={'Salveaza'}
            onClick={() => formik.handleSubmit()}
            disabled={isLoading}
          />
        </Grid>
      </Grid>
    </Modal>
  )
}

export default SensitiveProduct
